import { Controller } from "@hotwired/stimulus"

// Reports that somebody read a page, and for how long.
//
// A VISIT IS NOT CREATED UNTIL THEY HAVE STAYED A SECOND. The original
// asked for a row the moment the page loaded, which counts a bounce, a
// mis-click and a crawler alike. Here the row is not requested until a full
// second of ACTIVE time has passed, so those produce nothing at all — not a
// row with a zero in it. The table is then a record of reading rather than of
// requests, and the numbers are smaller and mean more.
//
// ACTIVE time is not time since load: a tab sitting behind another one does
// not accrue, because `activeSince` is cleared whenever the document hides.
// That is also what makes the one-second threshold honest — a page opened in a
// background tab and never looked at never reaches it.
export default class extends Controller {
  static values = { createUrl: String, path: String, minimumSeconds: Number }

  connect() {
    // Turbo paints a cached preview before the real page; only the real render
    // is a reading.
    this.previewing = document.documentElement.hasAttribute("data-turbo-preview")
    if (this.previewing) return

    this.activeMilliseconds = 0
    this.activeSince = document.hidden ? null : performance.now()
    this.durationUrl = null
    this.lastSentSeconds = null
    this.created = false

    this.boundVisibilityChange = this.visibilityChanged.bind(this)
    this.boundPageHide = this.pageHidden.bind(this)
    document.addEventListener("visibilitychange", this.boundVisibilityChange)
    window.addEventListener("pagehide", this.boundPageHide)

    // Polled rather than a single timeout, because a timeout cannot be paused:
    // active time only accrues while the document is visible, so the moment
    // the threshold is crossed depends on how the reader has behaved.
    this.qualifyTimer = window.setInterval(() => this.createIfStayed(), 250)
    this.heartbeat = window.setInterval(() => this.sendDuration(), 15000)
  }

  disconnect() {
    if (this.previewing) return

    this.finishActivePeriod()
    // Leaving before the threshold means no row was ever asked for, and none
    // is asked for now.
    this.sendDuration()
    document.removeEventListener("visibilitychange", this.boundVisibilityChange)
    window.removeEventListener("pagehide", this.boundPageHide)
    window.clearInterval(this.qualifyTimer)
    window.clearInterval(this.heartbeat)
  }

  createIfStayed() {
    if (this.created) return
    if (this.activeMilliseconds + this.currentPeriodMilliseconds() < this.minimumMilliseconds()) return

    this.created = true
    window.clearInterval(this.qualifyTimer)
    this.createVisit()
  }

  minimumMilliseconds() {
    return (this.hasMinimumSecondsValue ? this.minimumSecondsValue : 1) * 1000
  }

  async createVisit() {
    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({ path: this.pathValue }),
        credentials: "same-origin"
      })
      // 204 is the server declining: a signed-in admin, or a machine known to
      // be one. Not an error, and nothing more to send.
      if (!response.ok || response.status === 204) return

      const visit = await response.json()
      this.durationUrl = visit.duration_url
      this.sendDuration()
    } catch (_error) {
      // Analytics must never interfere with the public page.
    }
  }

  visibilityChanged() {
    if (document.hidden) {
      this.finishActivePeriod()
      this.sendDuration()
    } else if (this.activeSince === null) {
      this.activeSince = performance.now()
    }
  }

  pageHidden() {
    this.finishActivePeriod()
    this.sendDuration()
  }

  finishActivePeriod() {
    if (this.activeSince === null) return

    this.activeMilliseconds += performance.now() - this.activeSince
    this.activeSince = null
  }

  currentPeriodMilliseconds() {
    return this.activeSince === null ? 0 : performance.now() - this.activeSince
  }

  currentDurationSeconds() {
    return Math.floor((this.activeMilliseconds + this.currentPeriodMilliseconds()) / 1000)
  }

  sendDuration() {
    if (!this.durationUrl) return

    const seconds = this.currentDurationSeconds()
    if (seconds === this.lastSentSeconds) return
    this.lastSentSeconds = seconds

    const form = new FormData()
    form.append("duration_seconds", seconds)
    form.append("authenticity_token", this.csrfToken())
    // sendBeacon survives the page going away, which a fetch does not.
    navigator.sendBeacon(this.durationUrl, form)
  }

  headers() {
    return {
      "Accept": "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": this.csrfToken()
    }
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
